import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { HateoasResourceService } from '@lagoshny/ngx-hateoas-client';
import { Resource as BaseResource } from '@lagoshny/ngx-hateoas-client';
import { first, lastValueFrom } from 'rxjs';
import { handle } from 'src/shared-utilities/async-helpers/promise-helpers';
import { ClientLanguageService } from 'src/shared-utilities/services/client-language.service';
import { SlidesStateNames } from '../../router/slides-states';
import { AgentDetails } from '../../shared/models/agent-resources/agent-resource';
import { DealStatuses } from '../../shared/models/deal-resources/enumerators';
import { ClientJourneyEngagementService } from '../../shared/services/deal/client-journey-engagement.service';
import { ResourceTree, ResourceTreeCriteria } from '../../shared/services/resource-tree';
import { DashboardBorrower } from '../models/dashboard-borrower.model';
import { DashboardDeal, DealProgressStates } from '../models/dashboard-deal.model';
import { DashboardView } from '../models/dashboard-view.model';
import { APP_CONFIG, AppConfig } from 'src/environments/app-environment.models';

// Found on: https://decipher.dev/30-seconds-of-typescript/docs/maxDate/
const maxDate = (dates: Array<Date>) => new Date(Math.max(...dates.map(Number)));

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly appConfig = inject<AppConfig>(APP_CONFIG);
  private readonly cjEngagementService = inject(ClientJourneyEngagementService);
  private readonly auth0Service = inject(AuthService);
  private readonly httpClient = inject(HttpClient);
  private readonly resourceService = inject(HateoasResourceService);
  private readonly clientLanguageService = inject(ClientLanguageService);

  async trySaveDeal(agent: AgentDetails, loanCode: string, dealKey: string) {
    const [isAuthenticated, isAuthenticatedErr] = await handle(
      lastValueFrom(this.auth0Service.isAuthenticated$.pipe(first())),
    );
    if (isAuthenticatedErr) return;
    if (!isAuthenticated) return;

    const url = `${this.appConfig.publicApiBaseUrl}/v2/dashboard/deals/${loanCode}/save?dealKey=${dealKey}&shortCode=${agent.shortCode}`;
    const [_, saveDashboardDealError] = await handle(lastValueFrom(this.httpClient.post(url, null)));
    if (saveDashboardDealError) {
      console.error(saveDashboardDealError);
    }
  }

  private resourceTree: ResourceTree<DashboardView>;
  private async buildResourceTree(shortCode: string): Promise<ResourceTree<DashboardView>> {
    const rootUrl = `view/?shortCode=${shortCode}`;
    const treeCriteria = new ResourceTreeCriteria(DashboardView, rootUrl)
      .include('$.agent')
      .include('$.agent.contact')
      .include('$.user')
      .include('$.ceInvites', { isArray: true })
      .include('$.ceInvites.agent')
      .include('$.ceInvites.agent.contact')
      .include('$.deals', { isArray: true })
      .include('$.deals.documents', {
        isArray: true,
        params: { lang: this.clientLanguageService.clientLanguage },
      })
      .include('$.deals.subjectPropertyAddress')
      .include('$.deals.agent')
      .include('$.deals.agent.contact')
      .include('$.deals.borrowers', { isArray: true })
      .include('$.deals.borrowers.cjEngagement')
      .include('$.deals.borrowers.cjEngagement.pages', {
        isArray: true,
        link: 'cjEngagementPages',
      });

    this.resourceTree = new ResourceTree(treeCriteria, this.resourceService);
    return this.resourceTree;
  }

  // Example:
  // Path: '$.borrowers.contact' and contacts for all borrowers will be loaded
  async loadPath(root: DashboardView, path: string) {
    await this.resourceTree.loadPath(root, path);
  }

  // Example:
  // Path: '$.borrowers.contact' and parent borrowers (1...*) would load the contact
  // and contact child resources for those borrowers
  // Note parents are required.
  async loadPathForParents(path: string, parents: Array<BaseResource>) {
    await this.resourceTree.loadPathForParents(path, parents);
  }

  // Example:
  // Path: '$.borrowers.contact' will load all child data.
  // A resource is required.
  async loadResourceChildren(path: string, resources: Array<BaseResource>) {
    await this.resourceTree.loadResourceChildren(path, resources);
  }

  // Example:
  // Path: '$.borrowers.contact' returns an array of all contact objects (not grouped by borrower)
  getResources(root: DashboardView, path: string) {
    return this.resourceTree.getResources(root, path);
  }

  // Example:
  // Path: '$.borrowers.contact' returns an array of all borrower objects
  getParents(root: DashboardView, path: string) {
    return this.resourceTree.getParents(root, path);
  }

  async loadDashboardView(shortCode: string) {
    await this.buildResourceTree(shortCode);
    const view = await this.resourceTree.load();

    const deals = view.deals || [];
    for (const deal of deals) {
      this.resolveDates(deal);
      this.resolveDealProgressPercentages(deal);
      this.resolveLastViewed(deal);
      this.resolveProgressState(deal);
    }

    return view;
  }

  private resolveDates(deal: DashboardDeal) {
    deal.closingDate = deal.closingDate ? new Date(deal.closingDate) : null;
  }

  private resolveDealProgressPercentages(deal: DashboardDeal) {
    // TODO: The way we're calculating % totals across multiple borrowers
    // needs review. See the TODO block towards the bottom of this function
    // for an example.

    let totalBorrowerDealProgress = 0;
    let totalBorrowerPortalProgress = 0;
    let borrowers = deal.borrowers || [];

    // TODO: Until we have borrower invites, we're only supporting
    // the first (primary) borrower
    borrowers = borrowers.filter(b => b.order == 1);

    // Safety check
    if (borrowers.length == 0) {
      deal.progressPercentage = 100;
      deal.portalProgressPercentage = 100;
      return;
    }

    for (const borrower of borrowers) {
      // Get the percentages. These will not be rounded until after the loop.

      const borrowerDealProgressPercentage = this.getBorrowerDealProgressPercentage(deal, borrower);

      // TODO: There's commented code in getBorrowerPortalProgressPercentage
      // for when we support more than just the one borrower
      const borrowerPortalProgressPercentage = this.getBorrowerPortalProgressPercentage(deal, borrower);

      // Add the amounts to the running totals
      totalBorrowerDealProgress += borrowerDealProgressPercentage;
      totalBorrowerPortalProgress += borrowerPortalProgressPercentage;

      // Assign rounded values to the borrower
      borrower.dealProgressPercentage = Math.round(borrowerDealProgressPercentage);
      borrower.portalProgressPercentage = Math.round(borrowerPortalProgressPercentage);
    }
    // TODO: This needs some review. Summing a % per borrower then
    // dividing it by the number of borrowers doesn't necessarily
    // produce an accurate value.
    // Example:
    //     Borrower one has uploaded 2 of 7 docs = ~30%
    //     Borrower two has uploaded 1 of 1 docs = 100%
    //     Sum = 130%, divided over the two borrowers = 65%
    //     Reality is more like 37% (3 of 8)
    deal.progressPercentage = Math.round(totalBorrowerDealProgress / borrowers.length);
    deal.portalProgressPercentage = Math.round(totalBorrowerPortalProgress / borrowers.length);
  }

  private getBorrowerDealProgressPercentage(deal: DashboardDeal, borrower: DashboardBorrower): number {
    if (deal.status != DealStatuses.Lead) {
      return 100;
    }

    const borrowerDealProgressPercentage = this.cjEngagementService.getProgressPercentage(borrower.cjEngagement.pages);
    return borrowerDealProgressPercentage;
  }

  private getBorrowerPortalProgressPercentage(deal: DashboardDeal, borrower: DashboardBorrower): number {
    if (!deal.documents) return 100;

    const borrowerDocuments = deal.documents || [];

    // TODO: filter the documents when the dashboard handles
    // more than just the primary borrower.
    // let borrowerDocuments = deal.documents.filter(d => {
    //   let isBorrowerDocument = false;
    //   isBorrowerDocument ||= borrower.order == 1 && !d.borrowerId;
    //   isBorrowerDocument ||= borrower.borrowerId == d.borrowerId;
    //   return isBorrowerDocument;
    // });
    const completedBorrowerDocuments = borrowerDocuments.filter(d => d.hasFile);

    const totalBorrowerDocumentsCount = borrowerDocuments.length;
    const completedBorrowerDocumentsCount = completedBorrowerDocuments.length;

    if (!totalBorrowerDocumentsCount) return 100;

    const borrowerPortalProgressPercentage = (completedBorrowerDocumentsCount / totalBorrowerDocumentsCount) * 100;
    return borrowerPortalProgressPercentage;
  }

  private resolveLastViewed(deal: DashboardDeal) {
    const borrowers = deal.borrowers || [];
    for (const borrower of borrowers) {
      this.resolveBorrowerLastViewed(borrower);
    }
  }

  private resolveBorrowerLastViewed(borrower: DashboardBorrower) {
    let lastActiveUtc: Date;
    const pages = borrower.cjEngagement?.pages || [];
    for (const page of pages) {
      const pageLastActiveUtc = new Date(page.lastActiveUtc);
      lastActiveUtc ||= pageLastActiveUtc;
      lastActiveUtc = maxDate([lastActiveUtc, pageLastActiveUtc]);
    }
    borrower.lastActiveUtc = lastActiveUtc;
  }

  private resolveProgressState(deal: DashboardDeal) {
    if (deal.status == DealStatuses.Lead) {
      const primaryBorrower = deal.borrowers?.filter(b => b.order == 1)[0];
      const hasReachedReviewState = primaryBorrower?.cjEngagement?.pages.some(
        p => p.stateName == SlidesStateNames.Review,
      );
      deal.progressState = hasReachedReviewState ? DealProgressStates.BorrowerReview : DealProgressStates.InProgress;
    }

    if (deal.status == DealStatuses.New || deal.status == DealStatuses.Declined) {
      const requiresDocuments = deal.documents.some(d => !d.hasFile);
      deal.progressState = requiresDocuments ? DealProgressStates.DocumentsCollection : DealProgressStates.AgentReview;
    }

    if (deal.status == DealStatuses.Submitted) {
      deal.progressState = DealProgressStates.Submitted;
    }

    if (deal.status >= DealStatuses.Approved && deal.status < DealStatuses.Complete) {
      deal.progressState = DealProgressStates.Closing;
    }

    if (deal.status == DealStatuses.Complete) {
      deal.progressState = DealProgressStates.Complete;
    }
  }
}
